<script setup lang="ts">
import { PV } from "@/widgets";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.mjs?url";
import { nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

const props = defineProps<{
    src: string;
}>();

const MIN_ZOOM = 30;
const MAX_ZOOM = 150;
const ZOOM_STEP = 10;

const viewWrapRef = useTemplateRef("viewWrap");
const pageWrapRef = useTemplateRef("pageWrap");
const canvasRef = useTemplateRef("canvas");
const canvasWidth = ref(0);
const canvasHeight = ref(0);

const pageNumber = ref(0);
const maxPageNumber = ref(0);
const zoomPercentage = ref(100);
const zoomPercentageFit = ref(0);

const isReady = ref(false);
const isLoadingPage = ref(false);

function onFirstPage() {
    pageNumber.value = 1;
}

function onPrevPage() {
    if (pageNumber.value <= 1) return;
    pageNumber.value -= 1;
}

function onNextPage() {
    if (pageNumber.value >= maxPageNumber.value) return;
    pageNumber.value += 1;
}

function onLastPage() {
    pageNumber.value = maxPageNumber.value;
}

function onZoomIn() {
    if (zoomPercentage.value >= MAX_ZOOM) return;

    zoomPercentage.value += ZOOM_STEP;
    if (zoomPercentage.value > MAX_ZOOM) {
        zoomPercentage.value = MAX_ZOOM;
    }
}

function onZoomOut() {
    if (zoomPercentage.value <= MIN_ZOOM) return;

    zoomPercentage.value -= ZOOM_STEP;
    if (zoomPercentage.value < MIN_ZOOM) {
        zoomPercentage.value = MIN_ZOOM;
    }
}

function onZoomReset() {
    zoomPercentage.value = 100;
}

function onZoomFit() {
    zoomPercentage.value = zoomPercentageFit.value;
}

onMounted(async () => {
    const pdfjs = await import("pdfjs-dist");
    pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;
    const pdf = await pdfjs.getDocument({ url: props.src }).promise;

    pageNumber.value = 1;
    maxPageNumber.value = pdf.numPages;
    isReady.value = true;
    await nextTick();

    const canvas = canvasRef.value;
    if (canvas === null) return;
    const viewWrap = viewWrapRef.value;
    if (viewWrap === null) return;
    const pageWrap = pageWrapRef.value;
    if (pageWrap === null) return;

    watch(
        [pageNumber, zoomPercentage],
        async ([pageNum, zoom]) => {
            const page = await pdf.getPage(pageNum);

            const viewWrapRect = viewWrap.getBoundingClientRect();
            const pageWrapRect = pageWrap.getBoundingClientRect();
            const deltaWidth = viewWrapRect.width - pageWrapRect.width;
            const deltaHeight = viewWrapRect.height - pageWrapRect.height;
            const pageRect = page.getViewport({ scale: 1 });

            if (pageRect.width > pageRect.height) {
                // Landscape
                const ratio = viewWrapRect.width / (pageRect.width + deltaWidth * 2);
                zoomPercentageFit.value = Math.floor(ratio * 100);
            } else {
                // Portrait
                const ratio = viewWrapRect.height / (pageRect.height + deltaHeight * 2);
                zoomPercentageFit.value = Math.floor(ratio * 100);
            }

            const scale = zoom / 100;
            const viewport = page.getViewport({ scale: scale });

            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            canvas.style.width = Math.floor(viewport.width) + "px";
            canvas.style.height = Math.floor(viewport.height) + "px";

            canvasWidth.value = canvas.width;
            canvasHeight.value = canvas.height;

            isLoadingPage.value = true;
            await page.render({ canvas: canvas, viewport: viewport }).promise;
            isLoadingPage.value = false;
        },
        { immediate: true },
    );
});
</script>

<template>
    <div class="border border-gray-400 w-full h-[calc(100vh*4/5)] flex flex-col">
        <div class="flex md:flex-row flex-col justify-between border-b border-b-gray-400 p-2">
            <div class="flex flex-row gap-2 items-center justify-center">
                <PV.Button
                    v-tooltip.top="'First Page'"
                    :disabled="pageNumber <= 1 || isLoadingPage || !isReady"
                    icon="pi pi-step-backward"
                    severity="contrast"
                    text
                    @click="onFirstPage"
                />
                <PV.Button
                    v-tooltip.top="'Prev Page'"
                    :disabled="pageNumber <= 1 || isLoadingPage || !isReady"
                    icon="pi pi-caret-left"
                    severity="contrast"
                    text
                    @click="onPrevPage"
                />
                <div class="w-16 text-center">{{ pageNumber }} / {{ maxPageNumber }}</div>
                <PV.Button
                    v-tooltip.top="'Next Page'"
                    :disabled="pageNumber >= maxPageNumber || isLoadingPage || !isReady"
                    icon="pi pi-caret-right"
                    severity="contrast"
                    text
                    @click="onNextPage"
                />
                <PV.Button
                    v-tooltip.top="'Last Page'"
                    :disabled="pageNumber >= maxPageNumber || isLoadingPage || !isReady"
                    icon="pi pi-step-forward"
                    severity="contrast"
                    text
                    @click="onLastPage"
                />
            </div>

            <div class="flex flex-row items-center justify-center gap-2">
                <PV.Button
                    v-tooltip.top="'Reset Zoom'"
                    :disabled="!isReady"
                    icon="pi pi-refresh"
                    severity="contrast"
                    text
                    @click="onZoomReset"
                />
                <PV.Button
                    v-tooltip.top="'Zoom Out'"
                    :disabled="zoomPercentage <= MIN_ZOOM || !isReady"
                    icon="pi pi-minus"
                    severity="contrast"
                    text
                    @click="onZoomOut"
                />
                <span class="w-16 text-center">{{ zoomPercentage }}%</span>
                <PV.Button
                    v-tooltip.top="'Zoom In'"
                    :disabled="zoomPercentage >= MAX_ZOOM || !isReady"
                    icon="pi pi-plus"
                    severity="contrast"
                    text
                    @click="onZoomIn"
                />
                <PV.Button
                    v-tooltip.top="'Fit Page'"
                    :disabled="!isReady"
                    icon="pi pi-expand"
                    severity="contrast"
                    text
                    @click="onZoomFit"
                />
            </div>
        </div>

        <div v-if="isReady" ref="viewWrap" class="relative overflow-auto grow bg-gray-200">
            <div ref="pageWrap" class="absolute z-0 top-0 left-0 right-0 bottom-0 m-2"></div>
            <div class="relative z-1 inline-flex justify-center items-center min-w-full min-h-full p-2">
                <canvas v-show="!isLoadingPage" ref="canvas" class="pointer-events-none"></canvas>
                <div
                    v-show="isLoadingPage"
                    class="bg-white flex items-center justify-center text-gray-500 text-lg text-center"
                    :style="{ width: `${canvasWidth}px`, height: `${canvasHeight}px` }"
                >
                    MEMUAT HALAMAN, HARAP TUNGGU ...
                </div>
            </div>
        </div>
        <div v-else class="bg-white h-full flex items-center justify-center text-gray-900 text-2xl text-center">
            MEMUAT BERKAS, HARAP TUNGGU ...
        </div>
    </div>
</template>
